import React from "react";
import { ButtonWithLoader } from "@src/components/atoms/buttons/button-with-loader/button-with-loader";
import closeIcon from "@src/assets/svg/close-icon-gray.svg";
import styles from "./ota-accept-and-push-firmware-modal.module.scss";
import { FlexColumnContainer } from "@src/components/templates/layouts/flex-column-container/flex-column-container";
import { OTASecondaryButton } from "@src/components/atoms/buttons/ota-secondary-button/ota-secondary-button";
import { IconButton } from "@src/components/atoms/buttons/icon-button/icon-button";
import { ActiveInstallationProcessPayload } from "@src/types/ota-update";
import { useOtaAcceptAndPushFirmwareModal } from "./use-ota-accept-and-push-firmware-modal";
import type { InstallAndActionBlock } from "@src/cloud-connect/ota-update/constants";

const cssClassName = "ota-accept-and-push-firmware-modal";

const OTAAcceptAndPushFirmwareModal = ({
  data,
  setInstallAndActionBlock,
}: {
  data: ActiveInstallationProcessPayload;
  setInstallAndActionBlock: React.Dispatch<
    React.SetStateAction<InstallAndActionBlock | null>
  >;
}) => {
  const {
    firmware,
    machine,
    folders,
    target_path_folders,
    texts,
    targetPath,
    handleCloseModal,
    handleAcceptAndPushFirmware,
    isAcceptAndPushLoading,
  } = useOtaAcceptAndPushFirmwareModal(data, setInstallAndActionBlock);

  const targetPathPlaceholder = "{targetPath}";
  const [
    targetFoldersPrefix = texts.foldersSectionTitle,
    targetFoldersSuffix = "",
  ] = texts.foldersSectionTitle.split(targetPathPlaceholder);
  const hasTargetPathPlaceholder = texts.foldersSectionTitle.includes(
    targetPathPlaceholder,
  );

  return (
    <div className={styles[cssClassName]}>
      <div
        className={styles[`${cssClassName}__overlay`]}
        aria-label={texts.closeModalAriaLabel}
      />
      <div className={styles[`${cssClassName}__dialog-wrapper`]}>
        <div
          className={styles[`${cssClassName}__modal-body`]}
          role="dialog"
          aria-modal="true"
        >
          <div className={styles[`${cssClassName}__header`]}>
            <h2 className={styles[`${cssClassName}__title`]}>{texts.title}</h2>
            <IconButton
              fun={handleCloseModal}
              icon={closeIcon}
              tooltipPosition="top"
              variant="close-button"
              disabled={isAcceptAndPushLoading}
            />
          </div>

          <div className={styles[`${cssClassName}__form`]}>
            <FlexColumnContainer verticalGap={20} alignItems="stretch">
              <FlexColumnContainer verticalGap={15} alignItems="stretch">
                <p className={styles[`${cssClassName}__section-title`]}>
                  {texts.firmwareSectionTitle}
                </p>
                <div className={styles[`${cssClassName}__info-card`]}>
                  <p className={styles[`${cssClassName}__firmware-name`]}>
                    {firmware.fileName}
                    <span className={styles[`${cssClassName}__firmware-size`]}>
                      ({firmware.fileSize})
                    </span>
                  </p>
                  <p className={styles[`${cssClassName}__meta-text`]}>
                    {texts.uploadedAtLabel} {firmware.uploadedAt}
                  </p>
                </div>
              </FlexColumnContainer>

              <FlexColumnContainer verticalGap={15} alignItems="stretch">
                <p className={styles[`${cssClassName}__section-title`]}>
                  {texts.machineSectionTitle}
                </p>
                <div className={styles[`${cssClassName}__machine-card`]}>
                  <p className={styles[`${cssClassName}__machine-name`]}>
                    {machine.name}
                    <span className={styles[`${cssClassName}__machine-ip`]}>
                      ({machine.ip})
                    </span>
                  </p>
                </div>
              </FlexColumnContainer>
              <FlexColumnContainer verticalGap={15} alignItems="stretch">
                <p className={styles[`${cssClassName}__section-title`]}>
                  {texts.firmwarePackageFoldersSectionTitle}
                </p>
                <div
                  className={`${styles[`${cssClassName}__folders-list-card`]} ${
                    styles[`${cssClassName}__folders-card`]
                  }`}
                >
                  {folders?.map((entry) => (
                    <p
                      key={`package-folder-${entry}`}
                      className={styles[`${cssClassName}__folder-name`]}
                    >
                      \{entry}
                    </p>
                  ))}
                </div>
              </FlexColumnContainer>

              <FlexColumnContainer verticalGap={15} alignItems="stretch">
                <p className={styles[`${cssClassName}__section-title`]}>
                  {hasTargetPathPlaceholder ? (
                    <>
                      {targetFoldersPrefix}
                      <span
                        className={styles[`${cssClassName}__target-path-bold`]}
                      >
                        {targetPath}
                      </span>
                      {targetFoldersSuffix}
                    </>
                  ) : (
                    texts.foldersSectionTitle
                  )}
                </p>
                <div
                  className={`${styles[`${cssClassName}__folders-list-card`]} ${
                    styles[`${cssClassName}__target_folders-card`]
                  }`}
                >
                  {target_path_folders?.map((entry) => (
                    <p
                      key={`target-folder-${entry}`}
                      className={styles[`${cssClassName}__folder-name`]}
                    >
                      \{entry}
                    </p>
                  ))}
                </div>
              </FlexColumnContainer>

              <p className={styles[`${cssClassName}__warning-text`]}>
                {texts.warningText}
              </p>
            </FlexColumnContainer>
          </div>

          <div className={styles[`${cssClassName}__buttons-wrapper`]}>
            <OTASecondaryButton
              fun={handleCloseModal}
              text={texts.cancelButtonText}
              disabled={isAcceptAndPushLoading}
            />
            <ButtonWithLoader
              fun={handleAcceptAndPushFirmware}
              text={texts.acceptAndPushButtonText}
              loading={isAcceptAndPushLoading}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default OTAAcceptAndPushFirmwareModal;
